import { useState, useCallback, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import UploadView from "./components/UploadView";
import LoadingView from "./components/LoadingView";
import Dashboard from "./components/Dashboard";
import CameraSession from "./components/CameraSession";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2000;

const sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

async function waitForAnalysis(id, onUpdate, signal) {
  while (!signal.aborted) {
    const response = await axios.get(`${API}/analysis/${id}`, { signal });
    const state = response.data;
    onUpdate(state);

    if (state.status === "complete") return state;
    if (state.status === "failed") {
      throw new Error(state.error || "Analysis failed. Please try another video.");
    }
    await sleep(POLL_INTERVAL_MS);
  }
  throw new DOMException("Analysis polling was cancelled", "AbortError");
}

async function loadCompletedAnalysis(id, onUpdate, signal) {
  const state = await waitForAnalysis(id, onUpdate, signal);
  const videoResponse = await axios.get(`${API}/analysis/${id}/video`, { signal });
  return { ...state.data, videoUrl: videoResponse.data.url };
}

function AppContent() {
  const [view, setView] = useState("upload");
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState("awaiting_upload");
  const [analysisData, setAnalysisData] = useState(null);
  const [error, setError] = useState(null);
  const activeRequest = useRef(null);
  const { resultId } = useParams();
  const navigate = useNavigate();

  const showState = useCallback((state) => {
    setStage(state.stage);
    setProgress(state.progress);
  }, []);

  useEffect(() => {
    if (!resultId) return undefined;

    const controller = new AbortController();
    activeRequest.current = controller;
    setView("loading");
    setError(null);
    loadCompletedAnalysis(resultId, showState, controller.signal)
      .then((data) => {
        setAnalysisData(data);
        setView("dashboard");
      })
      .catch((requestError) => {
        if (requestError.name === "CanceledError" || requestError.name === "AbortError") return;
        console.error("Failed to load analysis:", requestError);
        setError(requestError.response?.data?.detail || requestError.message || "This analysis has expired");
        setView("upload");
        navigate("/", { replace: true });
      });

    return () => controller.abort();
  }, [navigate, resultId, showState]);

  const handleFileUpload = useCallback(async (file) => {
    const controller = new AbortController();
    activeRequest.current?.abort();
    activeRequest.current = controller;
    setView("loading");
    setProgress(0);
    setStage("awaiting_upload");
    setError(null);

    try {
      const createResponse = await axios.post(`${API}/analyses`, {
        filename: file.name,
        content_type: file.type,
        byte_size: file.size,
      }, { signal: controller.signal });
      const created = createResponse.data;

      await axios.put(created.upload_url, file, {
        headers: created.upload_headers,
        signal: controller.signal,
        onUploadProgress: ({ loaded, total }) => {
          const uploadPercent = total ? Math.round((loaded / total) * 8) : 0;
          setProgress(uploadPercent);
        },
      });

      setStage("queued");
      setProgress(10);
      await axios.post(`${API}/analyses/${created.id}/start`, null, { signal: controller.signal });
      const data = await loadCompletedAnalysis(created.id, showState, controller.signal);
      setAnalysisData(data);
      navigate(`/results/${created.id}`, { replace: true });
      setView("dashboard");
    } catch (requestError) {
      if (requestError.name === "CanceledError" || requestError.name === "AbortError") return;
      console.error("Upload or analysis error:", requestError);
      setError(requestError.response?.data?.detail || requestError.message || "Upload failed");
      setView("upload");
    }
  }, [navigate, showState]);

  const handleNewAnalysis = () => {
    activeRequest.current?.abort();
    setView("upload");
    setAnalysisData(null);
    setProgress(0);
    setStage("awaiting_upload");
    setError(null);
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="app-container">
      {view === "upload" && <UploadView onUpload={handleFileUpload} error={error} />}
      {view === "loading" && <LoadingView progress={progress} stage={stage} />}
      {view === "dashboard" && analysisData && (
        <Dashboard data={analysisData} onNewAnalysis={handleNewAnalysis} />
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<AppContent />} />
        <Route path="/camera" element={<CameraSession />} />
        <Route path="/results/:resultId" element={<AppContent />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
